/**
 * The pilot's profile (Phase 5): callsign, level and XP, credits, rating with its tier or the
 * placement count, the win/loss record, and the last twenty matches with what each paid.
 * Everything comes from the server's records; the page computes nothing about money or rating.
 */
import { MECHS } from '@steelwake/shared/data';
import { MatchHistorySchema, type MatchHistoryRow } from '@steelwake/shared/matchmaking';
import { ProfileSchema, type Profile as ProfileView } from '@steelwake/shared/progression';
import { colors, fonts, radii, spacing } from '@steelwake/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { api, describeError } from '../api.js';

const MODE_LABELS = { casual: 'Casual', ranked: 'Ranked', botFill: 'Bot fill' } as const;
const OUTCOME_LABELS = { win: 'Win', loss: 'Loss', draw: 'Draw', none: 'Aborted' } as const;
const num = (n: number) => n.toLocaleString('en-US');

function Stat({
  label,
  value,
  sub,
  bar,
  testId,
}: {
  label: string;
  value: string;
  sub: ReactNode;
  bar?: number;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        padding: spacing.md,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, margin: '4px 0' }}>{value}</div>
      {bar !== undefined ? (
        <div
          style={{
            height: 6,
            background: colors.border,
            borderRadius: radii.sm,
            overflow: 'hidden',
            margin: '6px 0',
          }}
        >
          <div
            data-testid={`${testId}-bar`}
            style={{
              width: `${String(Math.round(bar * 100))}%`,
              height: '100%',
              background: colors.accent,
            }}
          />
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: colors.textMuted }}>{sub}</div>
    </div>
  );
}

export function Profile() {
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [history, setHistory] = useState<MatchHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [p, h] = await Promise.all([
          api<unknown>('/api/v1/profile'),
          api<unknown>('/api/v1/match/history'),
        ]);
        if (cancelled) return;
        setProfile(ProfileSchema.parse(p));
        setHistory(MatchHistorySchema.parse(h).matches);
      } catch (err) {
        if (!cancelled) setError(describeError(err));
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p role="alert" data-testid="profile-error" style={{ color: colors.danger }}>
        {error}
      </p>
    );
  }
  if (!profile || !history) {
    return (
      <p role="status" style={{ color: colors.textMuted, fontFamily: fonts.ui }}>
        Loading your profile…
      </p>
    );
  }
  const { level, rating, record } = profile;
  const placementShown = Math.min(rating.rankedMatches, rating.placementMatches);
  const ratingValue = rating.placed
    ? `${String(Math.round(rating.rating))} · ${rating.tier ?? ''}`
    : `Placement ${String(placementShown)}/${String(rating.placementMatches)}`;

  return (
    <div
      data-testid="profile"
      style={{ width: 'min(820px, 96vw)', fontFamily: fonts.ui, color: colors.text }}
    >
      <h1
        data-testid="profile-callsign"
        style={{ margin: `0 0 ${spacing.md}`, fontSize: 22, letterSpacing: '0.12em' }}
      >
        {profile.callsign ?? 'Pilot'}
      </h1>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: spacing.md,
          marginBottom: spacing.lg,
        }}
      >
        <Stat
          label="Level"
          testId="profile-level"
          value={String(level.level)}
          sub={level.capped ? 'max level' : `${num(level.xpIntoLevel)} / ${num(level.xpToNext)} XP`}
          bar={level.progress}
        />
        <Stat
          label="Credits"
          testId="profile-credits"
          value={num(profile.balances.credits)}
          sub={<Link to="/hangar">spend them in the hangar</Link>}
        />
        <Stat
          label="Rating"
          testId="profile-rating"
          value={ratingValue}
          sub={
            rating.placed
              ? `±${String(Math.round(rating.rd))} · ${String(rating.rankedMatches)} ranked`
              : `${String(rating.placementMatches)} ranked matches to place`
          }
        />
        <Stat
          label="Record"
          testId="profile-record"
          value={`${String(record.wins)} W · ${String(record.losses)} L`}
          sub={`${String(record.matches)} matches${record.draws > 0 ? `, ${String(record.draws)} drawn` : ''}`}
        />
      </div>
      <h2
        style={{
          margin: `0 0 ${spacing.sm}`,
          fontSize: 12,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        Recent matches
      </h2>
      <table
        data-testid="profile-history"
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
      >
        <thead>
          <tr style={{ color: colors.textMuted, textAlign: 'left' }}>
            <th style={{ padding: '6px 4px' }}>When</th>
            <th>Mode</th>
            <th>Result</th>
            <th>Mech</th>
            <th>Opponent</th>
            <th style={{ textAlign: 'right' }}>Credits</th>
            <th style={{ textAlign: 'right' }}>XP</th>
            <th style={{ textAlign: 'right' }}>Rating</th>
          </tr>
        </thead>
        <tbody>
          {history.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                data-testid="profile-no-matches"
                style={{ padding: spacing.md, color: colors.textMuted }}
              >
                No online matches yet.
              </td>
            </tr>
          ) : null}
          {history.map((m) => (
            <tr
              key={m.id}
              data-testid="profile-match"
              data-outcome={m.outcome}
              style={{ borderTop: `1px solid ${colors.border}` }}
            >
              <td style={{ padding: '6px 4px' }}>{new Date(m.endedAt).toLocaleString()}</td>
              <td>{MODE_LABELS[m.mode]}</td>
              <td
                style={{
                  color:
                    m.outcome === 'win'
                      ? colors.success
                      : m.outcome === 'loss'
                        ? colors.danger
                        : colors.textMuted,
                }}
              >
                {OUTCOME_LABELS[m.outcome]}
                {m.endReason === 'forfeit' ? ' (forfeit)' : ''}
              </td>
              <td>{MECHS[m.mechId]?.name ?? m.mechId}</td>
              <td>
                {m.opponent
                  ? `${m.opponent.callsign ?? 'Bot'}${m.opponent.bot ? ' (bot)' : ''}`
                  : '–'}
              </td>
              <td style={{ textAlign: 'right' }}>{m.settled ? `+${num(m.credits ?? 0)}` : '…'}</td>
              <td style={{ textAlign: 'right' }}>{m.settled ? `+${num(m.xp ?? 0)}` : '…'}</td>
              <td style={{ textAlign: 'right' }}>
                {m.ratingBefore !== null && m.ratingAfter !== null
                  ? `${String(Math.round(m.ratingBefore))} → ${String(Math.round(m.ratingAfter))}`
                  : '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
